import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  Card,
  ChevronDownIcon,
  ShieldIcon,
} from '../components/ui';
import { AiFlow } from '../components/AiFlow';
import { PlatformBadges } from '../components/PlatformBadges';
import { TrustStrip } from '../components/TrustStrip';
import { WhalePod } from '../components/BrandMark';
import { useMe } from '../hooks/useApiQuery';

export function HowItWorks() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const me = useMe();

  const feePct = Number(me.data?.platform_fee_pct ?? 20);
  const exampleFee = Math.round(100 * (feePct / 100));
  const exampleKeep = 100 - exampleFee;

  return (
    <div className="mx-auto max-w-app px-5 pt-2 pb-10">
      <button
        type="button"
        onClick={() => nav(-1)}
        className="pb-focus-ring mb-3 inline-flex items-center gap-1 rounded-pb-sm px-1 py-1 text-sm text-tg-hint hover:text-tg-text"
      >
        <ArrowLeftIcon size={16} />
        {t('common.back')}
      </button>

      {/* Hero — brand-heavy header with a pod illustration, not just text */}
      <div className="-mx-2 mb-6 flex items-center gap-3">
        <div className="shrink-0">
          <WhalePod size={150} />
        </div>
        <div className="min-w-0">
          <h1 className="pb-display text-[24px] leading-[1.1]">
            {t('how.title')}
          </h1>
          <p className="mt-2 text-[13px] leading-relaxed text-tg-hint">
            {t('how.intro')}
          </p>
        </div>
      </div>

      {/* Steps — each step has its own stylised mini-illustration */}
      <section className="space-y-3">
        <StepCard
          step={1}
          title={t('how.step1.title')}
          body={t('how.step1.body')}
          illustration={<DepositIllustration />}
        />
        <StepCard
          step={2}
          title={t('how.step2.title')}
          body={t('how.step2.body')}
          illustration={<PodIllustration />}
        />
        <StepCard
          step={3}
          title={t('how.step3.title')}
          body={t('how.step3.body')}
          illustration={<ChainIllustration />}
        />
        <StepCard
          step={4}
          title={t('how.step4.title')}
          body={t('how.step4.body')}
          illustration={<WithdrawIllustration />}
        />
      </section>

      {/* AI flow — moved here from Dashboard. Visual explainer of the pod */}
      <section className="mt-10">
        <AiFlow />
      </section>

      {/* Platforms — single heading, no duplication */}
      <section className="mt-10">
        <h2 className="pb-display text-[22px]">{t('platforms.heading')}</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-tg-hint">
          {t('how.platforms_note')}
        </p>
        <div className="mt-3">
          <PlatformBadges withoutHeading />
        </div>
      </section>

      {/* Fees — visual flow: +$100 → −$fee → =keep */}
      <section className="mt-10">
        <h2 className="pb-display text-[22px]">{t('how.fees.title')}</h2>
        <Card pad="lg" className="mt-3 pb-grain">
          <p className="text-[15px] leading-relaxed">
            {t('how.fees.body', { fee_pct: feePct })}
          </p>
          <div className="mt-4 flex items-center gap-2 rounded-pb-md border border-pb-border-soft p-3">
            <FeeCell
              label={t('how.fees.example_gain')}
              value={t('how.fees.example_gain_value')}
              tone="text-pb-success"
            />
            <FeeArrow />
            <FeeCell
              label={t('how.fees.example_fee')}
              value={t('how.fees.example_fee_value', { fee: exampleFee })}
              tone="text-tg-hint"
            />
            <FeeArrow />
            <FeeCell
              label={t('how.fees.example_keep')}
              value={t('how.fees.example_keep_value', { keep: exampleKeep })}
              tone="text-pb-accent"
              strong
            />
          </div>
          <p className="pb-caption mt-3">{t('how.fees.hwm_note')}</p>
        </Card>
      </section>

      <section className="mt-10">
        <h2 className="pb-display text-[22px]">{t('how.safety.title')}</h2>
        <Card pad="md" className="mt-3 flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-pb-accent-soft text-pb-accent">
            <ShieldIcon size={18} />
          </span>
          <p className="text-[14px] leading-relaxed text-tg-text">
            {t('how.safety.body')}
          </p>
        </Card>
      </section>

      <section className="mt-10">
        <h2 className="pb-display text-[22px]">{t('how.risks.title')}</h2>
        <Card
          pad="md"
          className="mt-3 border border-[color-mix(in_oklab,var(--pb-warn)_40%,transparent)] bg-[color-mix(in_oklab,var(--pb-warn)_8%,var(--pb-paper))]"
        >
          <p className="text-[14px] leading-relaxed">{t('how.risks.body')}</p>
        </Card>
      </section>

      <section className="mt-10">
        <h2 className="pb-display text-[22px]">{t('how.faq.title')}</h2>
        <div className="mt-3 divide-y divide-pb-border-soft rounded-pb-lg border border-pb-border-soft">
          {(
            [
              'faq_min',
              'faq_withdraw_time',
              'faq_withdraw_any_time',
              'faq_networks',
              'faq_stop',
              'faq_multiple',
              'faq_losses',
            ] as const
          ).map((k) => (
            <FaqItem key={k} question={t(`how.faq.${k}.q`)} answer={t(`how.faq.${k}.a`)} />
          ))}
        </div>
      </section>

      <div className="mt-10 flex flex-col items-stretch gap-3">
        <Button fullWidth onClick={() => nav('/wallet?tab=deposit')}>
          {t('welcome.cta')}
        </Button>
        <Button variant="ghost" fullWidth onClick={() => nav('/')}>
          {t('how.to_home')}
        </Button>
      </div>

      <TrustStrip />
    </div>
  );
}

/* ── Step card with side illustration ────────────────────────── */

function StepCard({
  step,
  title,
  body,
  illustration,
}: {
  step: number;
  title: string;
  body: string;
  illustration: React.ReactNode;
}) {
  return (
    <Card pad="md">
      <div className="flex items-start gap-3">
        <div className="shrink-0">{illustration}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="pb-display text-[13px] text-pb-accent">
              {step}
            </span>
            <div className="text-[15px] font-semibold">{title}</div>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-tg-hint">
            {body}
          </p>
        </div>
      </div>
    </Card>
  );
}

/* ── Tiny illustrations per step (all 48×48 SVG) ─────────────── */

function DepositIllustration() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden>
      <rect width="48" height="48" rx="12" fill="var(--pb-paper2)" />
      <g transform="translate(10, 12)">
        <rect x="0" y="6" width="28" height="18" rx="3" fill="none"
              stroke="var(--pb-accent)" strokeWidth="1.5"/>
        <circle cx="22" cy="15" r="2" fill="var(--pb-accent)" />
        <path d="M 14 0 L 14 6 M 10 4 L 14 0 L 18 4"
              stroke="var(--pb-accent)" strokeWidth="1.5" fill="none"
              strokeLinecap="round" strokeLinejoin="round"/>
      </g>
    </svg>
  );
}

function PodIllustration() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden>
      <rect width="48" height="48" rx="12" fill="var(--pb-paper2)" />
      <g transform="translate(6, 16)">
        {/* Three mini whales in formation */}
        <ellipse cx="10" cy="8"  rx="6" ry="4" fill="var(--pb-accent)" opacity="0.55"/>
        <ellipse cx="22" cy="12" rx="8" ry="5" fill="var(--pb-accent)" />
        <ellipse cx="32" cy="6"  rx="5" ry="3.5" fill="var(--pb-accent)" opacity="0.5"/>
      </g>
    </svg>
  );
}

function ChainIllustration() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden>
      <rect width="48" height="48" rx="12" fill="var(--pb-paper2)" />
      <g transform="translate(10, 12)" stroke="var(--pb-accent)"
         strokeWidth="1.5" fill="none" strokeLinejoin="round">
        <rect x="0" y="2" width="8" height="8" rx="2" />
        <rect x="10" y="10" width="8" height="8" rx="2" />
        <rect x="20" y="2" width="8" height="8" rx="2" />
        <path d="M 8 6 L 10 10 M 18 14 L 20 10" />
        <path d="M 2 18 h 24" strokeDasharray="2 3"/>
      </g>
    </svg>
  );
}

function WithdrawIllustration() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden>
      <rect width="48" height="48" rx="12" fill="var(--pb-paper2)" />
      <g transform="translate(10, 10)">
        <circle cx="14" cy="14" r="12" fill="none"
                stroke="var(--pb-accent)" strokeWidth="1.5"
                strokeDasharray="3 3"/>
        <path d="M 8 14 l 10 0 M 14 10 l 4 4 l -4 4"
              stroke="var(--pb-accent)" strokeWidth="1.8"
              strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </g>
    </svg>
  );
}

/* ── Fee calculator bits ─────────────────────────────────────── */

function FeeArrow() {
  return (
    <span aria-hidden className="shrink-0 text-tg-hint">
      <ArrowRightIcon size={14} />
    </span>
  );
}

function FeeCell({
  label,
  value,
  tone,
  strong,
}: {
  label: string;
  value: string;
  tone: string;
  strong?: boolean;
}) {
  return (
    <div className="flex-1 text-center">
      <div className="pb-eyebrow whitespace-nowrap">{label}</div>
      <div
        className={clsx(
          'pb-numeral mt-1 text-[18px] leading-none',
          tone,
          strong && 'font-semibold',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="pb-focus-ring flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
      >
        <span className="text-[15px] font-medium text-tg-text">{question}</span>
        <span
          aria-hidden
          className={clsx(
            'shrink-0 text-tg-hint transition-transform',
            open && 'rotate-180',
          )}
        >
          <ChevronDownIcon size={18} />
        </span>
      </button>
      {open ? (
        <p className="px-4 pb-4 text-[14px] leading-relaxed text-tg-hint">
          {answer}
        </p>
      ) : null}
    </div>
  );
}
